type ServiceMethod = "dine-in" | "takeaway" | "delivery";

export type RecommendedTipSetting = {
  type: "percentage" | "absolute";
  steps: number[];
};

export const DEFAULT_RECOMMENDED_TIP_SETTING: RecommendedTipSetting = {
  type: "percentage",
  steps: [5, 10, 15],
};

/** Quick amounts offered when a gift card or table credit is rung up. */
export const DEFAULT_GIFT_CARD_AMOUNTS: number[] = [50, 100, 200, 500, 1000];

type VacationScheduleItem = {
  date: string;
  reason?: string;
  guestNote?: string;
  payload:
    | {
        type: "closed_all_day";
      }
    | {
        // open on some intervals
        type: "intervals";
        // times when it's open on this date
        intervals: Array<{
          start: string; // "10:00" (local time)
          end: string; // "14:00" (local time)
          services?: Partial<Record<ServiceMethod, boolean>>; // interval-specific overrides
        }>;
      };
};
export type VacationSchedule = {
  items: VacationScheduleItem[];
};

export type NotificationSettingItem = {
  name: string;
  channels: { email: boolean; whatsapp: boolean; text: boolean };
};

export type NotificationSettings = {
  items: NotificationSettingItem[];
};

export type MenuPdfItem = {
  name: string;
  url: string;
  /** Asset row ID used to remove the uploaded file when the menu is deleted. */
  assetId?: string;
};

/**
 * Central registry of all restaurant setting keys.
 * Use this enum to ensure type safety when reading/writing settings.
 */
export enum RestaurantSettingKey {
  // Timing settings
  START_TIME = "start_time",
  END_TIME = "end_time",

  VACATION_SCHEDULE = "vacation_schedule",

  // Tip settings
  TIP_RECOMMENDED_AMOUNT_1 = "tip_recommended_amount_1",
  TIP_RECOMMENDED_AMOUNT_2 = "tip_recommended_amount_2",
  TIP_RECOMMENDED_AMOUNT_3 = "tip_recommended_amount_3",

  // Takeaway/Delivery settings
  TAKE_AWAY = "take_away",
  DELIVERY = "delivery",
  DELIVERY_CITIES = "delivery_cities",

  // Notification settings
  NOTIFICATION_SETTINGS = "notification_settings",

  // Table order settings
  REDUCE_QUANTITIES_UPDATE_KITCHEN_DEFAULT = "reduce_quantities_update_kitchen_default",
  // Lets a credited table send an order its credit does not cover; it goes on
  // the table's normal bill (docs/plans/table_credit).
  ALLOW_ORDERS_EXCEEDING_TABLE_CREDIT = "allow_orders_exceeding_table_credit",
  // Quick amounts (number[]) shown when a gift card or table credit is rung up.
  GIFT_CARD_AMOUNTS = "gift_card_amounts",

  // Mobile POS settings
  MOBILE_POS_ORDER_TYPES = "mobile_pos_order_types",

  // Tips settings
  RECOMMENDED_TIPS = "recommended_tips",

  // Reservation settings
  RESERVATIONS_ENABLED = "reservations_enabled",
  RESERVATIONS_AUTO_CONFIRM = "reservations_auto_confirm",
  RESERVATIONS_TIME_SLOT_MINUTES = "reservations_time_slot_minutes",
  RESERVATIONS_HIDE_DURATION_TO_GUESTS = "reservations_hide_duration_to_guests",
  RESERVATIONS_TIME_SLOT_GAP_MINUTES = "reservations_time_slot_gap_minutes",
  RESERVATIONS_MAX_ADVANCE_DAYS = "reservations_max_advance_days",
  RESERVATIONS_MIN_ADVANCE_HOURS = "reservations_min_advance_hours",
  RESERVATIONS_MIN_PARTY_SIZE = "reservations_min_party_size",
  RESERVATIONS_MAX_PARTY_SIZE = "reservations_max_party_size",
  RESERVATIONS_SERVICES = "reservations_services",
  RESERVATION_SCHEDULE = "reservation_schedule",
  RESERVATIONS_NOTE = "reservations_note",
  RESERVATIONS_CONFIRMATION_MESSAGE = "reservations_confirmation_message",
  RESERVATIONS_CANCELLATION_POLICY = "reservations_cancellation_policy",
  RESERVATIONS_REJECTION_MESSAGE = "reservations_rejection_message",
  RESERVATIONS_REQUIRE_DEPOSIT = "reservations_require_deposit",
  RESERVATIONS_DEPOSIT_AMOUNT = "reservations_deposit_amount",
  RESERVATIONS_CANCELLATION_DEADLINE_HOURS = "reservations_cancellation_deadline_hours",
  RESERVATION_ALGORITHM = "reservation_algorithm",
  RESERVATION_UNBOOKABLE_TABLE_IDS = "reservation_unbookable_table_ids",

  // Restaurant features (for customer-facing filters)
  RESTAURANT_FEATURES = "restaurant_features",

  // Menu PDFs (uploaded documents shown on the customer Menu tab)
  MENU_PDFS = "menu_pdfs",

  // Google review request settings
  REVIEW_REQUEST_ENABLED = "review_request_enabled",
  REVIEW_REQUEST_DELAY_HOURS = "review_request_delay_hours",
  REVIEW_REQUEST_MESSAGE = "review_request_message",
  REVIEW_REQUEST_MIN_DAYS_BETWEEN = "review_request_min_days_between",
}

/**
 * Type helper to get the value type for a specific setting key
 */
export type SettingValueType<K extends RestaurantSettingKey> =
  K extends RestaurantSettingKey.RESERVATIONS_ENABLED
    ? boolean
    : K extends RestaurantSettingKey.RESERVATIONS_AUTO_CONFIRM
      ? boolean
      : K extends RestaurantSettingKey.RESERVATIONS_HIDE_DURATION_TO_GUESTS
        ? boolean
        : K extends RestaurantSettingKey.RESERVATIONS_REQUIRE_DEPOSIT
          ? boolean
          : K extends RestaurantSettingKey.RESERVATIONS_TIME_SLOT_MINUTES
            ? number
            : K extends RestaurantSettingKey.RESERVATIONS_TIME_SLOT_GAP_MINUTES
              ? number
              : K extends RestaurantSettingKey.RESERVATIONS_MAX_ADVANCE_DAYS
                ? number
                : K extends RestaurantSettingKey.RESERVATIONS_MIN_ADVANCE_HOURS
                  ? number
                  : K extends RestaurantSettingKey.RESERVATIONS_MIN_PARTY_SIZE
                    ? number
                    : K extends RestaurantSettingKey.RESERVATIONS_MAX_PARTY_SIZE
                      ? number
                      : K extends RestaurantSettingKey.RESERVATIONS_DEPOSIT_AMOUNT
                        ? number
                        : K extends RestaurantSettingKey.RESERVATIONS_CANCELLATION_DEADLINE_HOURS
                          ? number
                          : K extends RestaurantSettingKey.RESERVATIONS_SERVICES
                            ? ReservationServicesSettings
                            : K extends RestaurantSettingKey.RESERVATION_ALGORITHM
                              ? "table_based" | "fixed_amount"
                              : K extends RestaurantSettingKey.RESERVATION_UNBOOKABLE_TABLE_IDS
                                ? number[]
                                : K extends RestaurantSettingKey.REDUCE_QUANTITIES_UPDATE_KITCHEN_DEFAULT
                                  ? boolean
                                  : K extends RestaurantSettingKey.RECOMMENDED_TIPS
                                    ? RecommendedTipSetting
                                    : K extends RestaurantSettingKey.GIFT_CARD_AMOUNTS
                                    ? number[]
                                    : K extends RestaurantSettingKey.MOBILE_POS_ORDER_TYPES
                                      ? Array<"table" | "dine-in" | "takeaway">
                                      : K extends RestaurantSettingKey.REVIEW_REQUEST_ENABLED
                                        ? boolean
                                        : K extends RestaurantSettingKey.REVIEW_REQUEST_DELAY_HOURS
                                          ? number
                                          : K extends RestaurantSettingKey.REVIEW_REQUEST_MIN_DAYS_BETWEEN
                                            ? number
                                            : K extends RestaurantSettingKey.DELIVERY_CITIES
                                              ? unknown[]
                                              : K extends RestaurantSettingKey.TAKE_AWAY
                                                ? "yes" | "no"
                                                : K extends RestaurantSettingKey.DELIVERY
                                                  ? "yes" | "no"
                                                  : K extends RestaurantSettingKey.MENU_PDFS
                                                    ? MenuPdfItem[]
                                                    : string | null;

/**
 * Default value for the menu_pdfs setting
 */
export const DEFAULT_MENU_PDFS: MenuPdfItem[] = [];

/**
 * Reservation schedule type
 */
export type ReservationSchedule = {
  [weekday: string]: {
    start_time: string;
    end_time: string;
    max_occupancy?: number;
    pre_payment?: number;
    auto_accept_seats?: number;
  }[];
};

/**
 * Reservation service periods configuration
 */
export interface ReservationServicesSettings {
  lunch: boolean;
  dinner: boolean;
}

/**
 * Full reservation settings configuration
 */
export interface ReservationSettings {
  reservations_enabled: boolean;
  reservations_auto_confirm: boolean;
  reservations_time_slot_minutes: number;
  reservations_hide_duration_to_guests: boolean;
  reservations_time_slot_gap_minutes: number;
  reservations_max_advance_booking_days: number;
  reservations_min_advance_booking_hours: number;
  reservations_min_party_size: number;
  reservations_max_party_size: number;
  reservations_services: ReservationServicesSettings;
  reservation_schedule: ReservationSchedule;
  reservations_note: string;
  reservations_confirmation_message: string;
  reservations_cancellation_policy: string;
  reservations_rejection_message: string;
  reservations_require_deposit: boolean;
  reservations_deposit_amount: number;
  reservations_cancellation_deadline_hours: number;
  reservation_algorithm: "table_based" | "fixed_amount";
  reservation_unbookable_table_ids: number[];
  review_request_enabled: boolean;
  review_request_delay_hours: number;
  review_request_message: string;
  review_request_min_days_between: number;
  menu_pdfs: MenuPdfItem[];
}

/**
 * Type alias for reservation settings form state
 * (same as ReservationSettings, kept for clarity in form contexts)
 */
export type ReservationSettingsFormState = ReservationSettings;

/**
 * Type-safe field names for reservation settings
 */
export type ReservationSettingsField = keyof ReservationSettings;

/**
 * Type-safe update handler for reservation settings
 */
export type ReservationSettingsUpdateHandler = <
  K extends ReservationSettingsField,
>(
  field: K,
  value: ReservationSettings[K],
) => void;

/**
 * Default reservation settings
 */
export const DEFAULT_RESERVATION_SETTINGS: ReservationSettings = {
  reservations_enabled: true,
  reservations_auto_confirm: true,
  reservations_time_slot_minutes: 60,
  reservations_hide_duration_to_guests: false,
  reservations_time_slot_gap_minutes: 15,
  reservations_max_advance_booking_days: 30,
  reservations_min_advance_booking_hours: 2,
  reservations_min_party_size: 1,
  reservations_max_party_size: 12,
  reservations_services: {
    lunch: true,
    dinner: true,
  },
  reservation_schedule: {},
  reservations_note:
    "Please note that your table will be held for 15 minutes after your reservation time.",
  reservations_confirmation_message:
    "Thank you for your reservation! We look forward to serving you.",
  reservations_cancellation_policy:
    "Cancellations must be made at least 24 hours in advance.",
  reservations_rejection_message:
    "Unfortunately, this time slot is fully booked. We'd love to welcome you at another time.",
  reservations_require_deposit: false,
  reservations_deposit_amount: 0,
  reservations_cancellation_deadline_hours: 24,
  reservation_algorithm: "fixed_amount",
  reservation_unbookable_table_ids: [],
  review_request_enabled: false,
  review_request_delay_hours: 3,
  review_request_message: "",
  review_request_min_days_between: 30,
  menu_pdfs: [],
};

/**
 * Predefined restaurant features for customer-facing filters.
 * Each restaurant selects which features apply to them via settings.
 */
export const RESTAURANT_FEATURES = [
  // Cuisine types
  {
    slug: "local-cuisine",
    name: "Local Cuisine",
    icon: "restaurant",
    group: "cuisine",
  },
  {
    slug: "european",
    name: "European Cuisine",
    icon: "lunch_dining",
    group: "cuisine",
  },
  {
    slug: "asian",
    name: "Asian Cuisine",
    icon: "ramen_dining",
    group: "cuisine",
  },
  {
    slug: "mediterranean",
    name: "Mediterranean",
    icon: "set_meal",
    group: "cuisine",
  },
  { slug: "fusion", name: "Fusion", icon: "auto_fix", group: "cuisine" },
  // Dietary
  { slug: "vegan", name: "Vegan", icon: "eco", group: "dietary" },
  {
    slug: "vegetarian",
    name: "Vegetarian",
    icon: "nutrition",
    group: "dietary",
  },
  // Amenities
  {
    slug: "outdoor-seating",
    name: "Outdoor Seating",
    icon: "sunny",
    group: "amenities",
  },
  { slug: "view", name: "View", icon: "landscape", group: "amenities" },
  {
    slug: "kid-friendly",
    name: "Kid-Friendly",
    icon: "face",
    group: "amenities",
  },
  {
    slug: "pet-friendly",
    name: "Pet-Friendly",
    icon: "pets",
    group: "amenities",
  },
  {
    slug: "parking",
    name: "Parking",
    icon: "local_parking",
    group: "amenities",
  },
  {
    slug: "live-music",
    name: "Live Music",
    icon: "music_note",
    group: "amenities",
  },
  {
    slug: "smoking-area",
    name: "Smoking Area",
    icon: "smoking_rooms",
    group: "amenities",
  },
  // Experience
  {
    slug: "business-lunch",
    name: "Business Lunch",
    icon: "business",
    group: "experience",
  },
  {
    slug: "corporate-gathering",
    name: "Corporate Gathering",
    icon: "groups",
    group: "experience",
  },
  {
    slug: "breakfast-menu",
    name: "Breakfast Menu",
    icon: "breakfast_dining",
    group: "experience",
  },
  {
    slug: "date-night",
    name: "Date Night",
    icon: "nightlife",
    group: "experience",
  },
  {
    slug: "special-occasions",
    name: "Special Occasions",
    icon: "celebration",
    group: "experience",
  },
  {
    slug: "open-kitchen",
    name: "Open Kitchen",
    icon: "cooking",
    group: "experience",
  },
] as const;

export type RestaurantFeatureSlug =
  (typeof RESTAURANT_FEATURES)[number]["slug"];
